import _ from "lodash";
import React from "react";
import moment from "moment";
import { RadioButton, DatePicker } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { graphql, compose } from "lib/apolloClient";
import { formatDate } from "Utils/DateTimeHelpers";
import { getOrganizationId } from "modules/CommonSelectors";
import { getChatMessagesForPreviewQuery } from "MessagingV2/modules/MessagingQuery";
import { MESSAGE_TYPE } from "MessagingV2/modules/Utils";
import withLoader from "UIComponents/GqlWithLoader";

import ConversationsPreview, {
  getMessagesMemoize,
} from "MessagingV2/components/ChatToChannel/ConversationsPreview";
import classes from "./PreviewChatWithOptions.scss";

const START_DATE_DISPLAY_FORMAT = "MMMM Do, YYYY";

export const RADIO_OPTIONS = {
  NO_CONVERSATION_HISTORY: "NO_CONVERSATION_HISTORY",
  TODAY: "TODAY",
  BEGINNING: "BEGINNING",
  CUSTOM: "CUSTOM",
};

const RADIO_OPTIONS_SEQUENCE = [
  RADIO_OPTIONS.NO_CONVERSATION_HISTORY,
  RADIO_OPTIONS.TODAY,
  RADIO_OPTIONS.BEGINNING,
  RADIO_OPTIONS.CUSTOM,
];

const getOptionLabel = ({ option, t, startDate }) => {
  switch (option) {
    case RADIO_OPTIONS.NO_CONVERSATION_HISTORY:
      return t("common:dont_include_any_conversation_history");
    case RADIO_OPTIONS.TODAY:
      return t("common:from_today");
    case RADIO_OPTIONS.BEGINNING: {
      const date = startDate
        ? formatDate({
            date: startDate,
            format: START_DATE_DISPLAY_FORMAT,
          })
        : "";
      return t("common:from_beginning_with_date", {
        date,
      });
    }
    case RADIO_OPTIONS.CUSTOM:
      return t("common:custom");
    default:
      return "";
  }
};

const PreviewChat = ({
  chatId,
  selectedState,
  setSelectedState,
  startDate,
}) => {
  const { t } = useI18n(["common"]);

  const { option: selectedOption, date: selectedDate } = selectedState;

  const onChangeDate = date => {
    setSelectedState({
      option: RADIO_OPTIONS.CUSTOM,
      date,
    });
  };

  const onOptionChange = option => {
    switch (option) {
      case RADIO_OPTIONS.NO_CONVERSATION_HISTORY:
        setSelectedState({
          option: RADIO_OPTIONS.NO_CONVERSATION_HISTORY,
          date: null,
        });
        break;
      case RADIO_OPTIONS.TODAY:
        setSelectedState({
          option: RADIO_OPTIONS.TODAY,
          date: moment(),
        });
        break;
      case RADIO_OPTIONS.BEGINNING:
        setSelectedState({
          option: RADIO_OPTIONS.BEGINNING,
          date: startDate,
        });
        break;
      case RADIO_OPTIONS.CUSTOM:
        setSelectedState({
          option: RADIO_OPTIONS.CUSTOM,
          date: null,
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.optionsWithDateSelector}>
        <div className={classes.optionsContainer}>
          {_.map(RADIO_OPTIONS_SEQUENCE, option => {
            const label = getOptionLabel({ option, t, startDate });
            const onSelectOption = () => onOptionChange(option);

            return (
              <RadioButton
                key={option}
                checked={_.isEqual(selectedOption, option)}
                onClick={onSelectOption}
              >
                {label}
              </RadioButton>
            );
          })}
        </div>
        <div className={classes.datePicker}>
          <DatePicker
            disabled={!_.isEqual(selectedOption, RADIO_OPTIONS.CUSTOM)}
            size="medium"
            name="date"
            value={
              _.isEqual(selectedOption, RADIO_OPTIONS.CUSTOM)
                ? selectedDate
                : null
            }
            onChange={onChangeDate}
            minDate={moment(startDate)}
            maxDate={moment()}
          />
        </div>
      </div>
      <div className={classes.previewContainer}>
        <ConversationsPreview selectedDate={selectedDate} chatId={chatId} />
      </div>
    </div>
  );
};

export default compose(
  graphql(getChatMessagesForPreviewQuery, {
    name: "getChatMessagesForPreviewQuery",
    options: ({ chatId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: chatId,
          organizationId: getOrganizationId(),
          input: {
            first: 1,
            messageType: [MESSAGE_TYPE.NORMAL],
            orderByDirection: "ASC",
          },
        },
      };
    },
    props: ({ getChatMessagesForPreviewQuery }) => {
      const { networkStatus, node } = getChatMessagesForPreviewQuery;
      const messagesEdges = getMessagesMemoize(
        _.get(node, "messagesV2.edges", [])
      );

      const firstMessage = _.get(messagesEdges, "[0].node", {});
      const startDate = _.get(firstMessage, "createdAt", moment());
      const isLoading = _.includes([1, 2], networkStatus);

      // Basically show loader every time in cache-and-network if
      // the list is empty, to avoid flickering(from empty state to date state)
      const isData =
        !_.isEmpty(node) && (!_.isEmpty(messagesEdges) || !isLoading);

      return {
        startDate,
        isLoading,
        isData,
      };
    },
  }),
  withLoader
)(PreviewChat);
